/**
 * DNASettings - Project DNA configuration tab
 * 
 * High-level business/risk settings:
 * - Target Audience & Tenancy
 * - Compliance & Risk Profile
 * - Delivery Lifecycle
 * - Security Posture
 * - Scale Strategy
 * - Quality & Testing
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';

const DNASettings = ({ config, options, isOwner, saving, onUpdate }) => {
  if (!options) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const handleRadioChange = (field, value) => {
    if (!isOwner) return;
    onUpdate({ [field]: value });
  };

  const handleCheckboxChange = (field, value, checked, isExclusive = false, setting = null) => {
    if (!isOwner) return;
    const current = config?.[field] || [];
    
    // If this is an exclusive option (like "none") and it's being checked
    if (isExclusive && checked) {
      // Clear all others and set only this one
      onUpdate({ [field]: [value] });
      return;
    }
    
    // If a non-exclusive option is being checked, remove any exclusive option
    if (checked && setting?.has_none_option) {
      const exclusiveValues = setting.options
        .filter(opt => opt.exclusive)
        .map(opt => opt.value);
      
      const withoutExclusive = current.filter(v => !exclusiveValues.includes(v));
      onUpdate({ [field]: [...withoutExclusive, value] });
      return;
    }
    
    // Standard checkbox behavior
    const updated = checked
      ? [...current, value]
      : current.filter(v => v !== value);
    onUpdate({ [field]: updated });
  };

  // Check if an exclusive option is selected (like "none")
  const isExclusiveSelected = (field, setting) => {
    if (!setting?.has_none_option) return false;
    const current = config?.[field] || [];
    return setting.options.some(opt => opt.exclusive && current.includes(opt.value));
  };

  return (
    <div className="space-y-6" data-testid="dna-settings">
      {Object.entries(options).map(([key, setting]) => (
        <Card key={key} className="relative">
          {saving && (
            <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-lg">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{setting.label}</CardTitle>
            <CardDescription>{setting.question}</CardDescription>
          </CardHeader>
          <CardContent>
            {setting.type === 'radio' ? (
              <RadioGroup
                value={config?.[key] || ''}
                onValueChange={(value) => handleRadioChange(key, value)}
                disabled={!isOwner || saving}
                className="space-y-3"
              >
                {setting.options.map((opt) => (
                  <div key={opt.value} className="flex items-start space-x-3">
                    <RadioGroupItem 
                      value={opt.value} 
                      id={`${key}-${opt.value}`}
                      data-testid={`dna-${key}-${opt.value}`}
                    />
                    <div className="grid gap-0.5">
                      <Label 
                        htmlFor={`${key}-${opt.value}`}
                        className="font-medium cursor-pointer"
                      >
                        {opt.label}
                      </Label>
                      {opt.description && (
                        <p className="text-sm text-gray-500">{opt.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </RadioGroup>
            ) : setting.type === 'checkbox' ? (
              <div className="space-y-3">
                {setting.options.map((opt) => {
                  const exclusiveSelected = isExclusiveSelected(key, setting);
                  const isThisExclusive = opt.exclusive;
                  // Disable non-exclusive options when an exclusive option is selected
                  const isDisabled = !isOwner || saving || (exclusiveSelected && !isThisExclusive);
                  
                  return (
                    <div key={opt.value} className="flex items-center space-x-3">
                      <Checkbox
                        id={`${key}-${opt.value}`}
                        checked={(config?.[key] || []).includes(opt.value)}
                        onCheckedChange={(checked) => handleCheckboxChange(key, opt.value, checked, isThisExclusive, setting)}
                        disabled={isDisabled}
                        data-testid={`dna-${key}-${opt.value}`}
                      />
                      <Label 
                        htmlFor={`${key}-${opt.value}`}
                        className={`font-medium cursor-pointer ${isDisabled && !isThisExclusive ? 'text-gray-400' : ''}`}
                      >
                        {opt.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DNASettings;
